import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Icons } from '@/components/ui/icons';
import { toast } from '@template/lib/toast';
import { useMutation } from 'convex/react';
import { api } from '@/lib/api';
import { trackDocumentUploadFailed, trackDocumentUploadSucceeded } from '@/lib/analytics';
import { dateOnlyKeyToTimestamp, isDateOnlyExpired } from '@/lib/berlin-time';

type DocumentUploadProps = {
  title: string;
  description?: string;
  documentType: 'approbation' | 'insurance' | 'facharzturkunde' | 'additional';
  onUploadComplete?: () => void;
  // Insurance-specific props
  requireExpiration?: boolean;
  validFrom?: string;
  validTo?: string;
  onValidFromChange?: (value: string) => void;
  onValidToChange?: (value: string) => void;
  company?: string;
  policyNumber?: string;
  onCompanyChange?: (value: string) => void;
  onPolicyNumberChange?: (value: string) => void;
};

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
  title,
  description,
  documentType,
  onUploadComplete,
  requireExpiration = false,
  validFrom = '',
  validTo = '',
  onValidFromChange,
  onValidToChange,
  company = '',
  policyNumber = '',
  onCompanyChange,
  onPolicyNumberChange,
}) => {
  const { t } = useTranslation('common');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const generateUploadUrl = useMutation(api.files.generateUploadUrl);
  const uploadApprobation = useMutation(api.app.verification.uploadApprobation);
  const uploadInsurance = useMutation(api.app.verification.uploadInsurance);
  const uploadFacharzturkunde = useMutation(api.app.verification.uploadFacharzturkunde);
  const uploadAdditionalDocument = useMutation(api.app.verification.uploadAdditionalDocument);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error(t('userProfile.documents.invalidFileType'));
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('userProfile.documents.fileSizeLimit'));
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error(t('userProfile.documents.selectFile'));
      return;
    }

    // Validate insurance-specific fields
    if (requireExpiration && documentType === 'insurance') {
      if (!company || !policyNumber || !validFrom || !validTo) {
        toast.error(t('userProfile.documents.fillInsuranceDetails'));
        return;
      }

      const expirationDate = dateOnlyKeyToTimestamp(validTo);
      if (expirationDate === null || isDateOnlyExpired(expirationDate)) {
        toast.error(t('userProfile.documents.insuranceFutureDate'));
        return;
      }
    }

    setUploading(true);

    try {
      // Step 1: Get upload URL
      const uploadUrl = await generateUploadUrl();

      // Step 2: Upload file to Convex storage
      const result = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': selectedFile.type },
        body: selectedFile,
      });

      if (!result.ok) {
        throw new Error(t('userProfile.documents.uploadFileFailed'));
      }

      const { storageId } = await result.json();

      // Step 3: Save document record
      if (documentType === 'approbation') {
        await uploadApprobation({
          storageId,
          name: selectedFile.name,
        });
        toast.success(t('userProfile.documents.approbationUploadSuccess'));
      } else if (documentType === 'insurance') {
        await uploadInsurance({
          storageId,
          company: company!,
          policyNumber: policyNumber!,
          validFrom: dateOnlyKeyToTimestamp(validFrom!)!,
          validTo: dateOnlyKeyToTimestamp(validTo!)!,
        });
        toast.success(t('userProfile.documents.insuranceUploadSuccess'));
      } else if (documentType === 'facharzturkunde') {
        await uploadFacharzturkunde({
          storageId,
          name: selectedFile.name,
        });
        toast.success(t('userProfile.documents.facharztUploadSuccess'));
      } else if (documentType === 'additional') {
        await uploadAdditionalDocument({
          storageId,
          name: selectedFile.name,
        });
        toast.success(t('userProfile.documents.additionalDocumentUploadSuccess'));
      }

      trackDocumentUploadSucceeded({
        surface: 'verification_documents',
        document_type: documentType,
        file_count: 1,
      });

      // Reset form
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      trackDocumentUploadFailed(error, {
        surface: 'verification_documents',
        document_type: documentType,
        file_count: 1,
      });
      toast.error(error?.message || t('userProfile.documents.uploadError'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className='space-y-3'>
      <div>
        <Label className='text-sm font-medium'>{title}</Label>
        {description && <p className='text-xs text-muted-foreground mt-1'>{description}</p>}
      </div>

      {/* Insurance-specific fields */}
      {requireExpiration && documentType === 'insurance' && (
        <>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
            <div className='space-y-1.5'>
              <Label htmlFor='insurance-company' className='text-sm'>
                {t('userProfile.documents.insuranceCompany')}
              </Label>
              <Input id='insurance-company' value={company} onChange={(e) => onCompanyChange?.(e.target.value)} placeholder={t('userProfile.documents.insuranceCompanyPlaceholder')} />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='policy-number' className='text-sm'>
                {t('userProfile.documents.policyNumberLabel')}
              </Label>
              <Input id='policy-number' value={policyNumber} onChange={(e) => onPolicyNumberChange?.(e.target.value)} placeholder={t('userProfile.documents.policyNumberPlaceholder')} />
            </div>
          </div>

          <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
            <div className='space-y-1.5'>
              <Label htmlFor='valid-from' className='text-sm'>
                {t('userProfile.documents.validFrom')}
              </Label>
              <Input id='valid-from' type='date' value={validFrom} onChange={(e) => onValidFromChange?.(e.target.value)} />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='valid-to' className='text-sm'>
                {t('userProfile.documents.validTo')}
              </Label>
              <Input id='valid-to' type='date' value={validTo} onChange={(e) => onValidToChange?.(e.target.value)} />
            </div>
          </div>
        </>
      )}

      {/* File upload */}
      <div className='space-y-1.5'>
        <Label htmlFor={`file-${documentType}`} className='text-sm'>
          {t('userProfile.documents.document')}
        </Label>
        <div data-testid='document-upload-file-row' className='flex flex-col gap-2 sm:flex-row'>
          <Input id={`file-${documentType}`} ref={fileInputRef} type='file' accept='.pdf,.jpg,.jpeg,.png,.webp' onChange={handleFileSelect} disabled={uploading} className='w-full min-w-0 sm:flex-1' />
          <Button type='button' onClick={handleUpload} disabled={uploading || !selectedFile} className='min-h-11 w-full sm:min-h-0 sm:w-auto'>
            {uploading ? (
              <>
                <Icons.Loader className='h-4 w-4 animate-spin mr-2' />
                {t('userProfile.documents.uploading')}
              </>
            ) : (
              <>
                <Icons.Upload className='h-4 w-4 mr-2' />
                {t('userProfile.documents.upload')}
              </>
            )}
          </Button>
        </div>
        {selectedFile && <p className='break-words text-xs text-muted-foreground'>{t('userProfile.documents.selectedFile', { name: selectedFile.name, size: (selectedFile.size / 1024).toFixed(1) })}</p>}
        <p className='text-xs text-muted-foreground'>{t('userProfile.documents.maxFileSize')}</p>
      </div>
    </div>
  );
};
